<template>
	<div :class="rootClasses">
		<div class="app-tag-selector__header">
			<div class="app-tag-selector__search">
				<app-input
					v-model="searchQuery"
					:placeholder="placeholder"
					show-clear-button
					@clear-field="clearSearch"
				>
					<template #custom-icons>
						<app-tooltip
							v-if="props.allowCreate && props.mode === 'select' && searchQuery"
							text="Добавить тег"
							position="top"
						>
							<template #activator>
								<app-icon
									name="plus"
									width="16px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
									@click="createTag"
								/>
							</template>
						</app-tooltip>
					</template>
				</app-input>
				<div
					class="app-tag-selector__close"
					@click="emit('close')"
				>
					<app-icon
						class="app-tag-selector__close-icon"
						name="close"
						width="20px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
					/>
				</div>
			</div>

		</div>

		<span
			v-show="!searchQuery && hasSelection"
			class="app-tag-selector__reset"
			@click="resetSelection"
		>
			Сбросить
		</span>

		<div
			v-if="loading"
			class="app-tag-selector__body app-tag-selector__body--loading"
		>
			<app-loader />
		</div>

		<!-- Мод filter -->
		<div
			v-else-if="mode === 'filter'"
			class="app-tag-selector__body"
		>
			<template v-if="visibleCategories.length > 0">
				<div
					v-for="category in visibleCategories"
					:key="category.key"
					class="app-tag-selector__section"
				>
					<div class="app-tag-selector__section-title">
						<app-checkbox
							v-if="allowSelectGroup"
							:style="{ height: '16px', marginRight: '8px' }"
							:value="isCategoryAllSelected(category)"
							text=""
							@on-input="toggleCategory(category)"
						/>
						<div
							class="app-tag-selector__section-title-text"
							@click="allowSelectGroup ? toggleCategory(category) : undefined"
						>
							{{ category.label }}
						</div>
					</div>
					<div class="app-tag-selector__list">
						<div
							v-for="tag in getFilteredTags(category)"
							:key="tag.Id"
							class="app-tag-selector__tag"
						>
							<app-checkbox
								:style="{ height: '16px', marginRight: '8px' }"
								:value="isTagSelected(tag.Id)"
								text=""
								@on-input="toggleTag(tag)"
							/>
							<div
								class="app-tag-selector__tag-text"
								@click="toggleTag(tag)"
							>
								{{ formatTagLabel(tag) }}
							</div>
						</div>
					</div>
				</div>
			</template>

			<div v-else class="app-tag-selector__empty">
				{{ hasAnyTags ? 'Тег не найден' : 'Теги отсутствуют' }}
			</div>
		</div>

		<!-- Мод select (трехсекционный) -->
		<div
			v-else
			class="app-tag-selector__body app-tag-selector__body--columns"
		>
			<template v-if="visibleCategories.length > 0">
				<div class="app-tag-selector__columns">
					<div
						v-if="systemCategory && hasVisibleTags(systemCategory)"
						class="app-tag-selector__section app-tag-selector__section--system"
					>
						<div class="app-tag-selector__section-title">
							<app-checkbox
								v-if="allowSelectGroup"
								:style="{ height: '16px', marginRight: '8px' }"
								:value="isCategoryAllSelected(systemCategory)"
								text=""
								@on-input="toggleCategory(systemCategory)"
							/>
							<div
								class="app-tag-selector__section-title-text"
								@click="allowSelectGroup ? toggleCategory(systemCategory) : undefined"
							>
								{{ systemCategory.label }}
							</div>
						</div>
						<div class="app-tag-selector__list">
							<div
								v-for="tag in getFilteredTags(systemCategory)"
								:key="'sys-' + tag.Id"
								class="app-tag-selector__tag"
							>
								<app-checkbox
									:style="{ height: '16px', marginRight: '8px' }"
									:value="isTagSelected(tag.Id)"
									text=""
									@on-input="toggleTag(tag)"
								/>
								<div
									class="app-tag-selector__tag-text"
									@click="toggleTag(tag)"
								>
									{{ formatTagLabel(tag) }}
								</div>
							</div>
						</div>
					</div>

					<div
						v-if="(entityCategory && hasVisibleTags(entityCategory)) || (userCategory && hasVisibleTags(userCategory))"
						class="app-tag-selector__section-right"
					>
						<div
							v-if="entityCategory && hasVisibleTags(entityCategory)"
							:class="{
								'app-tag-selector__section': true,
								'app-tag-selector__section--entity': true,
								'app-tag-selector__section--full': !userCategory || !hasVisibleTags(userCategory)
							}"
						>
							<div class="app-tag-selector__section-title">
								<app-checkbox
									v-if="allowSelectGroup"
									:style="{ height: '16px', marginRight: '8px' }"
									:value="isCategoryAllSelected(entityCategory)"
									text=""
									@on-input="toggleCategory(entityCategory)"
								/>
								<div
									class="app-tag-selector__section-title-text"
									@click="allowSelectGroup ? toggleCategory(entityCategory) : undefined"
								>
									{{ entityCategory.label }}
								</div>
							</div>
							<div class="app-tag-selector__list">
								<div
									v-for="tag in getFilteredTags(entityCategory)"
									:key="'ent-' + tag.Id"
									class="app-tag-selector__tag"
								>
									<app-checkbox
										:style="{ height: '16px', marginRight: '8px' }"
										:value="isTagSelected(tag.Id)"
										text=""
										@on-input="toggleTag(tag)"
									/>
									<div
										class="app-tag-selector__tag-text"
										@click="toggleTag(tag)"
									>
										{{ formatTagLabel(tag) }}
									</div>
								</div>
							</div>
						</div>

						<div
							v-if="userCategory && hasVisibleTags(userCategory)"
							:class="{
								'app-tag-selector__section': true,
								'app-tag-selector__section--user': true,
								'app-tag-selector__section--full': !entityCategory || !hasVisibleTags(entityCategory)
							}"
						>
							<div class="app-tag-selector__section-title">
								<app-checkbox
									v-if="allowSelectGroup"
									:style="{ height: '16px', marginRight: '8px' }"
									:value="isCategoryAllSelected(userCategory)"
									text=""
									@on-input="toggleCategory(userCategory)"
								/>
								<div
									class="app-tag-selector__section-title-text"
									@click="allowSelectGroup ? toggleCategory(userCategory) : undefined"
								>
									{{ userCategory.label }}
								</div>
							</div>
							<div class="app-tag-selector__list">
								<div
									v-for="tag in getFilteredTags(userCategory)"
									:key="'usr-' + tag.Id"
									class="app-tag-selector__tag"
								>
									<app-checkbox
										:style="{ height: '16px', marginRight: '8px' }"
										:value="isTagSelected(tag.Id)"
										text=""
										@on-input="toggleTag(tag)"
									/>
									<div
										class="app-tag-selector__tag-text"
										@click="toggleTag(tag)"
									>
										{{ formatTagLabel(tag) }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>

			<div v-else class="app-tag-selector__empty">
				{{ hasAnyTags ? 'Тег не найден' : 'Теги отсутствуют' }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	onMounted
} from 'vue';
import { debounce } from 'lodash';
import AppCheckbox from '@/components/ui-kit/checkbox/AppCheckbox.vue';
import AppInput from '@/components/ui-kit/input/AppInput.vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import AppLoader from '@/components/common/AppLoader.vue';
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue';
import type { TagWithFrequency, TagContainer } from '@/models/interfaces/tags/tag-models';
import type { TagCategory } from './types';
import { TagEntityType } from '@/models/etc/types';
import { TagsApi } from '@/ api/TagsApi';

const entityLabels: Record<number, string> = {
	[TagEntityType.Person]: 'Все теги контактов',
	[TagEntityType.Client]: 'Все теги клиентов',
	[TagEntityType.Project]: 'Все теги проектов',
	[TagEntityType.File]: 'Все теги файлов',
	[TagEntityType.Job]: 'Все теги работ',
};

type AppTagSelectorProps = {
	modelValue?: TagWithFrequency[];
	categories?: TagCategory[];
	entityType?: TagEntityType;
	placeholder?: string;
	mode?: 'filter' | 'select';
	allowCreate?: boolean;
	allowSelectGroup?: boolean;
};

type AppTagSelectorEmits = {
	'update:modelValue': [tags: TagWithFrequency[]];
	'reset': [];
	'close': [];
	'create-tag': [name: string];
};

const props = withDefaults(defineProps<AppTagSelectorProps>(), {
	modelValue: () => [],
	categories: () => [],
	placeholder: 'Поиск',
	mode: 'filter',
	allowCreate: false,
	allowSelectGroup: false,
});

const emit = defineEmits<AppTagSelectorEmits>();

const searchQuery = ref('');
const debouncedQuery = ref('');
const loading = ref(false);
const tagContainer = ref<TagContainer | null>(null);

const applySearchQuery = debounce((value: string) => {
	debouncedQuery.value = value;
}, 300);

watch(searchQuery, value => applySearchQuery(value));

const clearSearch = () => {
	applySearchQuery.cancel();
	searchQuery.value = '';
	debouncedQuery.value = '';
};

const loadTags = async () => {
	if (props.entityType == null) return;
	loading.value = true;
	try {
		const data = await TagsApi.getTagsList(props.entityType);
		tagContainer.value = data as TagContainer;
	} catch (e) {
		console.log(e);
	} finally {
		loading.value = false;
	}
};

const buildCategoriesFromContainer = (container: TagContainer): TagCategory[] => {
	const label = entityLabels[props.entityType!] || 'Все теги';
	if (props.mode === 'filter') {
		return [{ key: 'entity', label, tags: container.OftenUsedForEntityTags }];
	}
	return [
		{ key: 'system', label: 'Все теги системы', tags: container.AllSystemTags },
		{ key: 'entity', label, tags: container.OftenUsedForEntityTags },
		{ key: 'user', label: 'Все ваши теги', tags: container.OftenUsedByUserTags },
	];
};

const resolvedCategories = computed<TagCategory[]>(() => {
	if (props.entityType != null && tagContainer.value) {
		return buildCategoriesFromContainer(tagContainer.value);
	}
	return props.categories;
});

onMounted(() => loadTags());
watch(() => props.entityType, () => loadTags());

const rootClasses = computed(() => ({
	'app-tag-selector': true,
	'app-tag-selector--select': props.mode === 'select',
	'app-tag-selector--two-columns': hasTwoColumns.value,
}));

const selectedIds = computed(() => new Set(props.modelValue.map(t => t.Id)));

const hasSelection = computed(() => props.modelValue.length > 0);

const systemCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'system')
);

const entityCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'entity')
);

const userCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'user')
);

const visibleCategories = computed(() =>
	resolvedCategories.value.filter(c => hasVisibleTags(c))
);

const matchesSearch = (tag: TagWithFrequency, query: string): boolean => {
	if (!query) return true;
	const lowerQuery = query.toLowerCase();
	const name = (tag.FullName || tag.Name).toLowerCase();
	return name.includes(lowerQuery);
};

const filteredByKey = computed<Record<string, TagWithFrequency[]>>(() => {
	const result: Record<string, TagWithFrequency[]> = {};
	for (const category of resolvedCategories.value) {
		result[category.key] = category.tags.filter(
			tag => tag.Frequency > 0 && matchesSearch(tag, debouncedQuery.value)
		);
	}
	return result;
});

const getFilteredTags = (category: TagCategory): TagWithFrequency[] => {
	return filteredByKey.value[category.key] ?? [];
};

const hasVisibleTags = (category: TagCategory): boolean => {
	return getFilteredTags(category).length > 0;
};

const hasTwoColumns = computed(() => {
	if (props.mode !== 'select') return false
	const hasLeft = !!(systemCategory.value && systemCategory.value.tags.some(t => t.Frequency > 0))
	const hasRight =
		!!(entityCategory.value && entityCategory.value.tags.some(t => t.Frequency > 0)) ||
		!!(userCategory.value && userCategory.value.tags.some(t => t.Frequency > 0))
	return hasLeft && hasRight
})

const hasAnyTags = computed(() => {
	return resolvedCategories.value.some(c => c.tags.some(t => t.Frequency > 0))
})

const formatTagLabel = (tag: TagWithFrequency): string => {
	return `${tag.Name.toLowerCase()} (${tag.Frequency})`;
};

const isTagSelected = (tagId: number): boolean => {
	return selectedIds.value.has(tagId);
};

const isCategoryAllSelected = (category: TagCategory): boolean => {
	const visibleTags = category.tags.filter(t => t.Frequency > 0);
	if (visibleTags.length === 0) return false;
	return visibleTags.every(t => selectedIds.value.has(t.Id));
};

const toggleTag = (tag: TagWithFrequency) => {
	const current = [...props.modelValue];
	const index = current.findIndex(t => t.Id === tag.Id);
	if (index >= 0) {
		current.splice(index, 1);
	} else {
		current.push(tag);
	}
	emit('update:modelValue', current);
};

const toggleCategory = (category: TagCategory) => {
	const visibleTags = category.tags.filter(t => t.Frequency > 0);
	const allSelected = isCategoryAllSelected(category);
	let current = [...props.modelValue];

	if (allSelected) {
		const categoryIds = new Set(visibleTags.map(t => t.Id));
		current = current.filter(t => !categoryIds.has(t.Id));
	} else {
		const existingIds = new Set(current.map(t => t.Id));
		for (const tag of visibleTags) {
			if (!existingIds.has(tag.Id)) {
				current.push(tag);
			}
		}
	}

	emit('update:modelValue', current);
};

const resetSelection = () => {
	emit('update:modelValue', []);
	emit('reset');
};

const createTag = () => {
	const name = searchQuery.value.trim();
	if (!name) {
		return;
	}
	emit('create-tag', name.toLowerCase());

	clearSearch();
};
</script>

<style lang="scss" scoped>
@import '@/styles/color.scss';

.app-tag-selector {
	position: relative;
	background: $background-white;
	font-family: 'Roboto', sans-serif;
	display: flex;
	flex-direction: column;
	width: 308px;
	height: 422px;
	max-height: 100%;
	overflow: hidden;

	&--two-columns {
		width: 616px;
	}

	@media (max-width: 768px) {
		width: 100% !important;
		height: 100% !important;
	}

	&__header {
		padding: 8px;
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex-shrink: 0;
	}

	&__search {
		display: flex;
		align-items: center;
		gap: 8px;

		:deep(.input-wrapper) {
			flex: 1;
		}
	}

	&__close {
		display: flex;
		justify-content: center;
		flex-shrink: 0;
		width: 24px;
		height: 24px;

		&-icon {
			margin-top: -3px;
		}
	}

	&__reset {
		position: absolute;
		right: 14px;
		top: 47px;
		font-size: 14px;
		line-height: 18px;
		color: $color-text-secondary;
		cursor: pointer;
		white-space: nowrap;
		user-select: none;
		z-index: 1;

		&:hover {
			color: $color-accent;
		}
	}

	&__body {
		padding: 0 8px;
		overflow-y: auto;
		flex: 1;
		min-height: 0;

		&--columns {
			display: flex;
			flex-direction: column;
			overflow: hidden;
		}

		&--loading {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 100px;
		}
	}

	&__columns {
		display: flex;
		flex-direction: row;
		gap: 1px;
		flex: 1;
		min-height: 0;
	}

	&__section {
		display: flex;
		flex-direction: column;
		min-height: 0;
	}

	&__section--system {
		flex: 1 1 50%;
		border-right: 1px solid $color-divider;
		padding-right: 8px;
		overflow: hidden;
	}

	&__section-right {
		flex: 1 1 50%;
		display: flex;
		flex-direction: column;
		min-height: 0;
		padding-left: 8px;
	}

	&__section--entity,
	&__section--user {
		flex: 1 1 50%;
		min-height: 0;
		overflow: hidden;
	}

	&__section--entity {
		border-bottom: 1px solid $color-divider;
		padding-bottom: 8px;
		margin-bottom: 8px;
	}

	&__section--full {
		flex: 1 1 100%;
	}

	&__section-title {
		display: flex;
		align-items: center;
		padding: 0;
		margin-bottom: 8px;
		user-select: none;
		flex-shrink: 0;
		cursor: pointer;

		&-text {
			font-size: 14px;
			font-weight: 500;
			line-height: 18px;
			color: #333;
		}
	}

	&__list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex: 1;
		min-height: 0;
	}

	&--select &__list {
		overflow-y: auto;
	}

	&__tag {
		display: flex;
		align-items: center;
		padding: 0;
		cursor: pointer;
		font-size: 14px;
		line-height: 18px;
		color: #333;
		transition: color 0.1s;

		&:hover &-text {
			text-decoration: underline;
			color: #396742;
		}

		&-text {
			font-size: 14px;
			line-height: 18px;
			word-break: break-word;
			white-space: normal;
		}
	}

	&__empty {
		padding: 12px 0;
		text-align: center;
		font-size: 14px;
		color: $color-text-secondary;
		flex-shrink: 0;
	}
}
</style>
